<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-xxl">
        <div v-if="isLoading" class="container-xxl">
          <Loader />
        </div>
        <template v-else>
          <div class="card mb-5 mb-xxl-8">
            <div class="card-header flex-wrap border-0 pt-6 pb-0">
              <div class="card-title d-flex align-items-center gap-3">
                <button type="button" class="btn btn-sm btn-icon btn-light" @click="() => router.push({ name: 'loyalty-programs' })">
                  <i class="fa fa-arrow-left"></i>
                </button>
                <h3 class="card-label mb-0">{{ formData.id ? formData.name : $t('loyalty.new') }}</h3>
              </div>
            </div>
            <div class="card-body">
              <div class="row">
                <Input containerClass="col-md-6 mb-5" type="text" name="loyalty-name" input-class="form-control-solid" size="lg"
                       :isRequired="true" v-model="formData.name" :label="$t('loyalty.name')" :placeholder="$t('loyalty.name_placeholder')" />

                <Select2 containerClass="col-md-6 mb-5" name="loyaltyShops" :label="$t('loyalty.shops')"
                         :multiple="true" :isRequired="true" v-model="formData.shop_ids">
                  <option v-for="shop in myShops" :key="shop.id" :value="shop.id">{{ shop.name }}</option>
                </Select2>

                <Select2 containerClass="col-md-6 mb-5" name="loyaltyEarningType" :label="$t('loyalty.earning_type')"
                         :multiple="false" :isRequired="true" v-model="formData.earning_type">
                  <option v-for="type in earningTypes" :key="type.value" :value="type.value">{{ type[locale] }}</option>
                </Select2>

                <div class="col-md-6 mb-5">
                  <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                    <span class="required">{{ earningRateLabel }}</span>
                  </label>
                  <input type="number" :min="formData.earning_type === 'per_purchase' ? 1 : 0.01" :step="formData.earning_type === 'per_purchase' ? 1 : 0.01" class="form-control form-control-solid" v-model.number="formData.earning_rate" required />
                  <div class="form-text">{{ earningRateHint }}</div>
                </div>

                <div class="col-md-6 mb-5">
                  <label class="d-flex align-items-center fs-6 fw-bold mb-2">{{ $t('loyalty.points_validity_days') }}</label>
                  <input type="number" min="1" class="form-control form-control-solid" v-model.number="formData.points_validity_days" />
                  <div class="form-text">{{ $t('loyalty.points_validity_hint') }}</div>
                </div>

                <div class="col-md-6 mb-5">
                  <label class="d-flex align-items-center fs-6 fw-bold mb-2">{{ $t('loyalty.status') }}</label>
                  <div class="form-check form-switch form-check-custom form-check-solid pt-2">
                    <input class="form-check-input" type="checkbox" v-model="formData.is_active" id="loyaltyIsActive">
                    <label class="form-check-label" for="loyaltyIsActive">{{ formData.is_active ? $t('loyalty.active') : $t('loyalty.inactive') }}</label>
                  </div>
                </div>
              </div>

              <div class="separator my-6"></div>

              <div class="d-flex justify-content-between align-items-center mb-3">
                <h4 class="mb-0">{{ $t('loyalty.products') }}</h4>
                <button type="button" class="btn btn-sm btn-light-primary" @click="openProductModal">
                  <i class="fa fa-boxes"></i> {{ $t('loyalty.add_products') }}
                </button>
              </div>
              <p class="text-muted fs-7">{{ $t('loyalty.products_hint') }}</p>
              <div v-if="selectedProductObjects.length" class="d-flex flex-wrap gap-2 mb-5">
                <span v-for="product in selectedProductObjects" :key="product.id" class="badge badge-light-primary p-3">
                  {{ product.name }}
                  <i class="fa fa-times ms-2 cursor-pointer" @click="removeProduct(product.id)"></i>
                </span>
              </div>
            </div>
            <div class="card-footer d-flex justify-content-end gap-2">
              <button type="button" @click="() => router.push({ name: 'loyalty-programs' })" class="btn btn-sm btn-light fw-bolder">{{ $t('button.cancel') }}</button>
              <button type="button" @click="saveProgram" class="btn btn-sm btn-primary fw-bolder" :disabled="isSaving">{{ $t('button.save') }}</button>
            </div>
          </div>

          <div v-if="formData.id" class="card mb-5 mb-xxl-8">
            <div class="card-header flex-wrap border-0 pt-6 pb-0">
              <div class="card-title"><h3 class="card-label">{{ $t('loyalty.rewards') }}</h3></div>
              <div class="card-toolbar gap-2 d-flex flex-wrap">
                <button type="button" class="btn btn-sm btn-light-primary" @click="addRewardRow">{{ $t('loyalty.add_reward') }}</button>
                <button type="button" class="btn btn-sm btn-primary" @click="saveAllRewards" :disabled="isSavingRewards || rewards.length === 0">
                  {{ $t('loyalty.save_all_rewards') }}
                </button>
              </div>
            </div>
            <div class="card-body pt-3">
              <div v-if="rewards.length === 0" class="text-muted text-center py-5">{{ $t('loyalty.empty') }}</div>
              <div v-else class="table-responsive">
                <table class="table align-middle gy-2 gs-2 loyalty-rewards-table" style="table-layout:fixed">
                  <colgroup>
                    <col style="width:26%">
                    <col style="width:9%">
                    <col style="width:23%">
                    <col style="width:23%">
                    <col style="width:9%">
                    <col style="width:10%">
                  </colgroup>
                  <thead>
                    <tr class="fw-bold text-muted fs-8 text-uppercase">
                      <th>{{ $t('loyalty.reward_label') }}</th>
                      <th>{{ $t('loyalty.threshold_points') }}</th>
                      <th>{{ $t('loyalty.reward_type') }}</th>
                      <th>{{ $t('loyalty.reward_value') }}</th>
                      <th>{{ $t('loyalty.max_uses_per_member') }}</th>
                      <th class="text-end">{{ $t('actions') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(reward, index) in rewards" :key="reward.id || 'new-' + index">
                      <td :data-label="$t('loyalty.reward_label')">
                        <input type="text" class="form-control form-control-sm" v-model="reward.label" :placeholder="$t('loyalty.reward_label_placeholder')" />
                      </td>
                      <td :data-label="$t('loyalty.threshold_points')">
                        <input type="number" min="1" class="form-control form-control-sm" v-model.number="reward.threshold_points" />
                      </td>
                      <td :data-label="$t('loyalty.reward_type')">
                        <Select2 containerClass="" selectClass="form-select-sm" :name="'rewardType' + index"
                                 :multiple="false" :isRequired="true" :modelValue="reward.reward_type"
                                 @update:modelValue="(value) => onRewardTypeChange(index, value)">
                          <option v-for="type in rewardTypes" :key="type.value" :value="type.value">{{ type[locale] }}</option>
                        </Select2>
                      </td>
                      <td :data-label="$t('loyalty.reward_value')">
                        <input v-if="['percentage', 'fixed_amount'].includes(reward.reward_type)"
                               type="number" min="0" step="0.01" class="form-control form-control-sm"
                               :title="reward.reward_type === 'percentage' ? $t('loyalty.reward_value_percentage_hint') : $t('loyalty.reward_value_fixed_hint')"
                               v-model.number="reward.reward_value" />
                        <Select2 v-else-if="reward.reward_type === 'free_product'" containerClass="" selectClass="form-select-sm"
                                 :name="'rewardProduct' + index" :multiple="false" :isRequired="true" v-model="reward.product_id">
                          <option v-for="product in simpleProducts" :key="product.id" :value="product.id">{{ product.name }}</option>
                        </Select2>
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td :data-label="$t('loyalty.max_uses_per_member')">
                        <input type="number" min="1" class="form-control form-control-sm" :title="$t('loyalty.max_uses_hint')" v-model.number="reward.max_uses_per_member" />
                      </td>
                      <td class="text-end">
                        <button type="button" class="btn btn-icon btn-sm btn-light-danger" @click="removeReward(index)">
                          <i class="fa fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div class="modal fade" id="loyaltyProductModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('loyalty.add_products') }}</h5>
            <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal"><i class="fa fa-times"></i></div>
          </div>
          <div class="modal-body">
            <Select2 containerClass="mb-5" name="loyaltyProductsSelect" :label="$t('loyalty.products')" :multiple="true"
                     :selectClass="'form-select form-control-lg form-control-solid form-select-solid min-h-45px'"
                     v-model="modalSelectedProducts">
              <optgroup v-for="(category, index) in getProductsByCategories" :key="category.category_id" :label="category.category_name">
                <option v-for="product in getProductsByCategories[index].sale_elements" :key="product.id" :value="product.id">{{ product.name }}</option>
              </optgroup>
            </Select2>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">{{ $t('button.cancel') }}</button>
            <button type="button" class="btn btn-primary" @click="confirmProducts" data-bs-dismiss="modal">{{ $t('button.save') }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import Input from '@/components/Input.vue'
import Select2 from '@/components/Select2.vue'
import Loader from '@/components/Loader.vue'

const store = useStore()
const route = useRoute()
const router = useRouter()

const isLoading = ref(true)
const isSaving = ref(false)
const locale = computed(() => i18n.global.locale)

const formData = ref({
  id: null,
  name: '',
  shop_ids: [],
  earning_type: 'per_purchase',
  earning_rate: null,
  points_validity_days: null,
  is_active: true,
  products: [],
})

const rewards = ref([])
const modalSelectedProducts = ref([])

const earningTypes = computed(() => store.getters['loyalty/getEarningTypes'])
const rewardTypes = computed(() => store.getters['loyalty/getRewardTypes'])
const myShops = computed(() => store.getters['loyalty/getMyShops'])

const earningRateLabel = computed(() => {
  return formData.value.earning_type === 'per_amount'
    ? i18n.global.t('loyalty.earning_rate_amount_label')
    : i18n.global.t('loyalty.earning_rate_purchase_label')
})

const earningRateHint = computed(() => {
  return formData.value.earning_type === 'per_amount'
    ? i18n.global.t('loyalty.earning_rate_amount_hint')
    : i18n.global.t('loyalty.earning_rate_purchase_hint')
})
const getProductsByCategories = computed(() => store.getters['products/getProductSaleElementsByCategories'])

const flatProducts = computed(() => {
  const list = []
  for (const category of (getProductsByCategories.value || [])) {
    for (const product of (category.sale_elements || [])) {
      list.push(product)
    }
  }
  return list
})

const selectedProductObjects = computed(() => flatProducts.value.filter((p) => formData.value.products.includes(p.id)))

const simpleProducts = computed(() => store.getters['loyalty/getSimpleProducts'])

function openProductModal() {
  modalSelectedProducts.value = [...formData.value.products]
  $('#loyaltyProductModal').modal('show')
}

function confirmProducts() {
  formData.value.products = [...modalSelectedProducts.value]
}

function removeProduct(id) {
  formData.value.products = formData.value.products.filter((p) => p !== id)
}

function addRewardRow() {
  // En tête de liste pour rester visible sans avoir à scroller.
  rewards.value.unshift({
    id: null,
    label: '',
    threshold_points: null,
    reward_type: 'percentage',
    reward_value: null,
    product_id: null,
    max_uses_per_member: null,
  })
}

function onRewardTypeChange(index, newType) {
  const reward = rewards.value[index]
  reward.reward_type = newType
  // On repart d'une valeur propre à chaque changement de type, pour éviter
  // qu'une ancienne valeur/produit reste sélectionné en arrière-plan.
  reward.reward_value = null
  reward.product_id = null
}

async function removeReward(index) {
  const reward = rewards.value[index]
  if (!reward.id) {
    rewards.value.splice(index, 1)
    return
  }
  Swal.fire({
    title: i18n.global.t('loyalty.confirm_delete_title'),
    text: i18n.global.t('loyalty.confirm_delete_reward_text'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: i18n.global.t('button.yesDelete'),
    cancelButtonText: i18n.global.t('button.cancel'),
  }).then(async (result) => {
    if (result.isConfirmed) {
      await store.dispatch('loyalty/deleteReward', { programId: formData.value.id, rewardId: reward.id })
      rewards.value.splice(index, 1)
      window.toastr['success'](i18n.global.t('loyalty.reward_deleted'))
    }
  })
}

function toNullableNumber(value) {
  if (value === '' || value === null || value === undefined) {
    return null
  }
  const number = Number(value)
  return Number.isNaN(number) ? null : number
}

async function saveReward(index, { silent = false } = {}) {
  const reward = rewards.value[index]
  const payload = {
    label: reward.label,
    threshold_points: toNullableNumber(reward.threshold_points),
    reward_type: reward.reward_type,
    reward_value: ['percentage', 'fixed_amount'].includes(reward.reward_type) ? toNullableNumber(reward.reward_value) : null,
    product_id: reward.reward_type === 'free_product' ? reward.product_id : null,
    max_uses_per_member: toNullableNumber(reward.max_uses_per_member),
  }
  try {
    if (reward.id) {
      const response = await store.dispatch('loyalty/updateReward', { programId: formData.value.id, rewardId: reward.id, data: payload })
      Object.assign(reward, response.data.reward)
    } else {
      const response = await store.dispatch('loyalty/createReward', { programId: formData.value.id, data: payload })
      Object.assign(reward, response.data.reward)
    }
    if (!silent) window.toastr['success'](i18n.global.t('loyalty.reward_saved'))
    return true
  } catch (err) {
    const message = err?.response?.data?.message
    if (!silent) window.toastr['error'](typeof message === 'string' ? message : i18n.global.t('loyalty.save_error'))
    return false
  }
}

const isSavingRewards = ref(false)

async function saveAllRewards() {
  if (!rewards.value.length) return
  isSavingRewards.value = true
  try {
    const results = await Promise.all(rewards.value.map((_, index) => saveReward(index, { silent: true })))
    const failedCount = results.filter((ok) => !ok).length
    if (failedCount === 0) {
      window.toastr['success'](i18n.global.t('loyalty.reward_saved'))
    } else {
      window.toastr['error'](i18n.global.t('loyalty.save_error'))
    }
  } finally {
    isSavingRewards.value = false
  }
}

async function saveProgram() {
  isSaving.value = true
  const payload = {
    name: formData.value.name,
    shop_ids: formData.value.shop_ids,
    earning_type: formData.value.earning_type,
    earning_rate: toNullableNumber(formData.value.earning_rate),
    points_validity_days: toNullableNumber(formData.value.points_validity_days),
    is_active: formData.value.is_active,
    product_ids: formData.value.products,
  }
  try {
    if (formData.value.id) {
      await store.dispatch('loyalty/updateProgram', { id: formData.value.id, data: payload })
      window.toastr['success'](i18n.global.t('loyalty.updated'))
    } else {
      const response = await store.dispatch('loyalty/createProgram', payload)
      formData.value.id = response.data.program.id
      window.toastr['success'](i18n.global.t('loyalty.created'))
      router.replace({ name: 'loyalty-program-detail', params: { programId: formData.value.id } })
    }
  } catch (err) {
    const message = err?.response?.data?.message
    window.toastr['error'](typeof message === 'string' ? message : i18n.global.t('loyalty.save_error'))
  } finally {
    isSaving.value = false
  }
}

async function loadProgram() {
  const programId = route.params.programId
  if (!programId) {
    return
  }
  await store.dispatch('loyalty/getProgram', programId)
  const program = store.getters['loyalty/getCurrentProgram']
  formData.value = {
    id: program.id,
    name: program.name,
    shop_ids: (program.shops || []).map((s) => s.id),
    earning_type: program.earning_type,
    earning_rate: Number(program.earning_rate),
    points_validity_days: program.points_validity_days,
    is_active: program.is_active,
    products: (program.products || []).map((p) => p.id),
  }
  rewards.value = (program.rewards || []).map((r) => ({
    ...r,
    reward_value: r.reward_value !== null ? Number(r.reward_value) : null,
  }))
}

onMounted(async () => {
  await Promise.all([
    store.dispatch('loyalty/loadEarningTypes'),
    store.dispatch('loyalty/loadRewardTypes'),
    store.dispatch('loyalty/loadMyShops'),
    store.dispatch('loyalty/loadSimpleProducts'),
    store.dispatch('products/loadAllProductSaleByCategories'),
    loadProgram(),
  ])
  isLoading.value = false
})
</script>

<style scoped>
/* Mobile : chaque palier devient une carte, champs empilés avec leur libellé */
@media (max-width: 767.98px) {
  .loyalty-rewards-table colgroup,
  .loyalty-rewards-table thead {
    display: none;
  }
  .loyalty-rewards-table,
  .loyalty-rewards-table tbody,
  .loyalty-rewards-table tr,
  .loyalty-rewards-table td {
    display: block;
    width: 100%;
  }
  .loyalty-rewards-table tr {
    border: 1px solid var(--kt-border-color, #eff2f5);
    border-radius: 0.65rem;
    padding: 0.75rem;
    margin-bottom: 1rem;
  }
  .loyalty-rewards-table td {
    padding: 0.35rem 0 !important;
    border: 0;
  }
  .loyalty-rewards-table td[data-label]::before {
    content: attr(data-label);
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--kt-text-muted, #a1a5b7);
    margin-bottom: 0.25rem;
  }
}
</style>
